import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import type { Order, OrderStatus } from '../types/order';
import type { Lesson, OfferingType } from '../types/lesson';
import { getUserProfile } from './userProfile';
import { createConversation } from './chat';

function convertTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const result = { ...data };
  for (const field of ['createdAt', 'updatedAt', 'completedAt']) {
    if (result[field] && typeof result[field] === 'object' && 'toDate' in (result[field] as object)) {
      result[field] = (result[field] as Timestamp).toDate().toISOString();
    }
  }
  return result;
}

/**
 * Derives order status from its lessons — always reflects reality regardless of
 * whether the cached `status` field on the Order document is stale.
 * An order is 'complete' only when every lesson in it has been delivered.
 */
export function computeOrderStatus(order: Order, lessons: Lesson[]): 'active' | 'complete' {
  const orderLessons = lessons.filter(l => l.orderId === order.id);
  if (orderLessons.length === 0) return 'active';

  // Training programs create lessons lazily (one per upload), so we can't rely on
  // "all existing lessons are delivered" — we must check against the total lesson count.
  if (order.type === 'training_program' && order.totalLessons) {
    const deliveredCount = orderLessons.filter(l => l.status === 'delivered').length;
    return deliveredCount >= order.totalLessons ? 'complete' : 'active';
  }

  return orderLessons.every(l => l.status === 'delivered') ? 'complete' : 'active';
}

export interface CreateOrderOptions {
  type: OfferingType;
  offeringId: string;
  offeringTitle: string;
  playerId: string;
  coachId: string;
  /** Per-lesson price in cents at time of booking. */
  priceCents: number;
  paymentMethod?: 'stripe' | 'credits';
  paymentIntentId?: string;
  creditTransactionId?: string;
  paymentStatus?: Order['paymentStatus'];
  platformFeeCents?: number;
  playerMessage?: string;
  autoRenew?: boolean;
  totalLessons?: number;  // For training_program: total lessons (e.g. 4)
  /** This order is hidden in My Lessons until the referenced order is complete. */
  appearsAfterOrderId?: string;
  /** Order was created via "Start my recurring plan" — no auto-renew prompt; backend auto-creates next on last delivery. */
  isRecurringPlan?: boolean;
  /**
   * When true, no initial lesson doc is created. Used for auto-renew orders paid during an active
   * program — the player uploads lesson 1 after the preceding order completes.
   */
  skipInitialLesson?: boolean;
  /**
   * When true, creates the first lesson with status 'reserved' (player paid but hasn't uploaded
   * a video yet). The lesson transitions to 'in_progress' once the player uploads their video.
   */
  reserveFirstLesson?: boolean;
}

/**
 * Creates a new order (and its first lesson) in Firestore.
 * Returns { orderId, lessonId, conversationId }.
 */
export async function createOrder(options: CreateOrderOptions): Promise<{
  orderId: string;
  lessonId: string;
  conversationId: string;
}> {
  const {
    type,
    offeringId,
    offeringTitle,
    playerId,
    coachId,
    paymentMethod,
    paymentIntentId,
    creditTransactionId,
    paymentStatus,
    platformFeeCents,
    playerMessage,
    autoRenew,
    totalLessons,
    appearsAfterOrderId,
    isRecurringPlan,
    skipInitialLesson,
    reserveFirstLesson,
  } = options;
  const { priceCents } = options;

  const [player, coach] = await Promise.all([
    getUserProfile(playerId),
    getUserProfile(coachId),
  ]);

  if (!player || !coach) throw new Error('User profiles not found');

  // For parent-type accounts (COPPA youth profile), the player on the order
  // is the youth profile attached to the parent's user doc — not the parent.
  // Coach surfaces render "Player: X (Parent: Y)" using these denormalized
  // fields so they don't need an extra read on each card.
  const isParentAccount = player.accountType === 'parent';
  const playerName = isParentAccount
    ? (player.playerFirstName?.trim() || 'Player')
    : (player.playerProfile?.displayName || player.displayName || 'Player');
  const playerAvatarUrl = player.playerProfile?.avatarUrl || player.avatarUrl;
  const playerAccountType: 'player' | 'parent' = isParentAccount ? 'parent' : 'player';
  const playerParentFirstName = isParentAccount ? player.parentFirstName?.trim() : undefined;
  const coachName = coach.coachProfile?.displayName || coach.displayName || 'Coach';
  const coachAvatarUrl = coach.coachProfile?.avatarUrl || coach.avatarUrl;

  const conversationId = await createConversation(playerId, coachId);

  const orderRef = doc(collection(db, 'orders'));
  const orderId = orderRef.id;

  // First lesson doc
  const lessonRef = doc(collection(db, 'lessons'));
  const lessonId = lessonRef.id;

  const orderData: Record<string, unknown> = {
    id: orderId,
    type,
    offeringId,
    offeringTitle,
    coachId,
    playerId,
    coachName,
    playerName,
    conversationId,
    lessonIds: [lessonId],
    priceCents,
    status: 'active' as OrderStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(coachAvatarUrl && { coachAvatarUrl }),
    ...(playerAvatarUrl && { playerAvatarUrl }),
    // COPPA: only denormalize the parent fields on parent-type accounts so
    // existing orders / non-parent accounts are unaffected.
    ...(isParentAccount && { playerAccountType }),
    ...(playerParentFirstName && { playerParentFirstName }),
    ...(paymentMethod && { paymentMethod }),
    ...(paymentIntentId && { paymentIntentId }),
    ...(creditTransactionId && { creditTransactionId }),
    ...(paymentStatus && { paymentStatus }),
    ...(platformFeeCents !== undefined && { platformFeeCents }),
    ...(playerMessage && { playerMessage }),
    ...(autoRenew !== undefined && { autoRenew }),
    ...(totalLessons !== undefined && { totalLessons }),
    ...(appearsAfterOrderId && { appearsAfterOrderId }),
    ...(isRecurringPlan && { isRecurringPlan }),
    // lessonIds is empty when skipInitialLesson is true (player uploads lesson 1 later)
    ...(skipInitialLesson && { lessonIds: [] }),
  };

  if (skipInitialLesson) {
    await setDoc(orderRef, orderData);
  } else {
    const lessonData: Record<string, unknown> = {
      id: lessonId,
      orderId,
      playerId,
      playerName,
      ...(playerAvatarUrl && { playerAvatarUrl }),
      coachId,
      coachName,
      ...(coachAvatarUrl && { coachAvatarUrl }),
      offeringId,
      offeringTitle,
      conversationId,
      lessonNumber: 1,
      priceCents,
      status: reserveFirstLesson ? 'reserved' : 'in_progress',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(reserveFirstLesson && { reservedAt: serverTimestamp() }),
      // Payment fields — needed for billing/earnings queries and transferToCoach
      ...(paymentMethod && { paymentMethod }),
      ...(paymentIntentId && { paymentIntentId }),
      ...(creditTransactionId && { creditTransactionId }),
      ...(paymentStatus && { paymentStatus }),
      ...(playerMessage && { playerMessage: undefined }), // playerMessage stays on order
    };
    // Strip undefined values — Firestore rejects them
    const cleanLessonData = Object.fromEntries(
      Object.entries(lessonData).filter(([, v]) => v !== undefined)
    );
    await Promise.all([
      setDoc(orderRef, orderData),
      setDoc(lessonRef, cleanLessonData),
    ]);
  }

  return { orderId, lessonId, conversationId };
}

/**
 * Gets an order by ID.
 */
export async function getOrder(orderId: string): Promise<Order | null> {
  const snap = await getDoc(doc(db, 'orders', orderId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) } as unknown as Order;
}

/**
 * Gets all orders for a conversation. Returned orders include any
 * training program whose lessons haven't yet been created (lazy lesson
 * creation), so the banner picker can see the program even on the
 * very first thread visit before the player has uploaded video #1.
 *
 * Per the orders security rule (firestore.rules:373), each returned
 * doc passes the per-doc `allow read` check (playerId / coachId / admin),
 * so a player querying here gets only orders they participate in.
 */
export async function getOrdersForConversation(conversationId: string): Promise<Order[]> {
  const q = query(
    collection(db, 'orders'),
    where('conversationId', '==', conversationId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }) as unknown as Order);
}

/**
 * Gets all orders for a player, newest first.
 */
export async function getOrdersForPlayer(playerId: string): Promise<Order[]> {
  const q = query(
    collection(db, 'orders'),
    where('playerId', '==', playerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => convertTimestamps(d.data()) as unknown as Order);
}

/**
 * Gets all orders for a coach, newest first.
 */
export async function getOrdersForCoach(coachId: string): Promise<Order[]> {
  const q = query(
    collection(db, 'orders'),
    where('coachId', '==', coachId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => convertTimestamps(d.data()) as unknown as Order);
}

/**
 * Subscribes to real-time order updates for a player.
 */
export function subscribeToOrdersForPlayer(
  playerId: string,
  callback: (orders: Order[]) => void,
  onError: (err: unknown) => void = (err) => console.error('[subscribeToOrdersForPlayer] query failed:', err),
): () => void {
  const q = query(
    collection(db, 'orders'),
    where('playerId', '==', playerId),
  );
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs
        .map((d) => convertTimestamps(d.data()) as unknown as Order)
        .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      callback(orders);
    },
    onError,
  );
}

/**
 * Subscribes to real-time order updates for a coach.
 */
export function subscribeToOrdersForCoach(
  coachId: string,
  callback: (orders: Order[]) => void
): () => void {
  const q = query(
    collection(db, 'orders'),
    where('coachId', '==', coachId),
  );
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs
        .map((d) => convertTimestamps(d.data()) as unknown as Order)
        .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      callback(orders);
    },
    (err) => console.error('[subscribeToOrdersForCoach] query failed:', err),
  );
}

/**
 * Marks an order as complete. Called by deliverLesson when all lessons are delivered.
 * Pass recurringRenewalPending: true for recurring-plan orders so the backend
 * (Cloud Function) knows to charge the saved payment method and create the next order.
 */
export async function completeOrder(
  orderId: string,
  options?: { recurringRenewalPending?: boolean },
): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    status: 'complete' as OrderStatus,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(options?.recurringRenewalPending && { recurringRenewalPending: true }),
  });
}

/**
 * Records the player's intent to auto-renew this training program.
 * Card is saved on file but NOT charged immediately — payment happens only after
 * the last lesson is delivered (via the recurringRenewalPending mechanism).
 * Suppresses the Auto Renew CTA for the rest of this order's life.
 */
export async function markOrderAutoRenewOptedIn(orderId: string): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    autoRenewOptedIn: true,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancels a previously confirmed auto-renew intent.
 * Safe to call at any time before the last lesson is delivered (no charge has been made).
 * After cancellation, the Auto Renew CTA re-appears so the player can opt in again.
 */
export async function cancelOrderAutoRenewIntent(orderId: string): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    autoRenewOptedIn: false,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Updates the autoRenew flag on a training program order.
 * @deprecated Use the AutoRenewConfirmModal CTA (which creates a real paid order) instead.
 */
export async function updateOrderAutoRenew(orderId: string, autoRenew: boolean): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    autoRenew,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Records a player-initiated refund request for a training program.
 * Does NOT call Stripe — the order is queued for admin review.
 * The order is hidden from the player's My Lessons page immediately (filtered by refundRequested: true).
 */
export async function requestOrderRefund(
  orderId: string,
  firstLessonId: string,
  reason?: string,
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, 'orders', orderId), {
    refundRequested: true,
    refundRequestedAt: serverTimestamp(),
    refundStatus: 'pending',
    updatedAt: serverTimestamp(),
    ...(reason && { refundRequestReason: reason }),
  });
  // Tag lesson 1 so it can be cross-referenced from the order if needed
  batch.update(doc(db, 'lessons', firstLessonId), {
    refundRequested: true,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Marks a training program order's refund as issued.
 * Called by admin after successfully running refundLessonFn.
 */
export async function markOrderRefundIssued(orderId: string): Promise<void> {
  await updateDoc(doc(db, 'orders', orderId), {
    refundStatus: 'issued',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Appends a new lessonId to an order's lessonIds array.
 * Used when unlocking the next lesson in a training program.
 */
export async function addLessonToOrder(orderId: string, lessonId: string): Promise<void> {
  const order = await getOrder(orderId);
  if (!order) throw new Error('Order not found');

  await updateDoc(doc(db, 'orders', orderId), {
    lessonIds: [...order.lessonIds, lessonId],
    updatedAt: serverTimestamp(),
  });
}
